import { FileDashedIcon, type Icon } from "@phosphor-icons/react"
import type { ReactNode } from "react"

import {
  LabelledActionButton,
  type LabelledAction,
} from "~/components/reusable/LabelledActionButton"
import { cn } from "~/utils/helpers"

export type EmptyStateAction = LabelledAction

/**
 * Centred "nothing here, and why" block for a section or card body: 24px icon (FileDashed by
 * default), title, up to 440px of description, one optional neutral button.
 * Canonical intent: {@link ../design-system/intents.ts EMPTY_STATE_INTENT}.
 * Approved surface: design-system/APPROVED.md.
 */
export const EmptyState = ({
  title,
  description,
  icon: IconComponent = FileDashedIcon,
  action,
  className,
}: {
  title: string
  description?: ReactNode
  icon?: Icon
  action?: EmptyStateAction
  className?: string
}) => {
  return (
    <div className={cn("flex flex-col items-center gap-2 px-6 py-12 text-center", className)}>
      <IconComponent size={24} aria-hidden className="text-fg-muted" />
      {title && <p className="header-h3 text-fg-base wrap-anywhere">{title}</p>}
      {description && (
        <p className="paragraph-sm text-fg-muted max-w-[440px] wrap-anywhere">{description}</p>
      )}
      {action && (
        <LabelledActionButton
          action={action}
          variant="neutral"
          // A long label wraps inside the card instead of running past its edges.
          className="mt-1 h-auto min-h-6 max-w-full whitespace-normal"
        />
      )}
    </div>
  )
}
